import { useMemo } from "react";
import { trpc } from "../trpc";
import { Implementation, ImplementationMeta } from "@zapier/bff";
import { App, useQueryApp } from "./useQueryApp";

export interface HydratedApp extends App {
  implementation?: Implementation;
  implementationMeta?: ImplementationMeta;
}

interface Input {
  slug?: string;
  selectedApi?: string;
  includeImplementation?: boolean;
}

export function useApp({
  slug,
  selectedApi,
  includeImplementation = false,
}: Input) {
  const { app, isLoading } = useQueryApp({
    slug,
    selectedApi,
  });

  const selectedApis = selectedApi
    ? [selectedApi]
    : app && "selectedApi" in app && app.selectedApi
      ? [app.selectedApi]
      : app && "currentImplementationId" in app && app.currentImplementationId
        ? [app.currentImplementationId]
        : [];

  const queryEnabled = selectedApis.length > 0 && includeImplementation;

  const { data: implementationData, isLoading: isLoadingImplementation } =
    trpc.implementation.implementations.useQuery(
      { selectedApis },
      { enabled: queryEnabled },
    );

  // Source of truth for fields that aren't on the /implementations response —
  // e.g. hasUpfrontFields, which downstream auth flows depend on.
  const {
    data: implementationMetaData,
    isLoading: isLoadingImplementationMeta,
  } = trpc.implementationMeta.getPrivateImplementationsMeta.useQuery(
    { selectedApis },
    {
      enabled: queryEnabled,
      // Match useQueryPrivateImplementationsMeta — opt out of batching so a
      // slower sibling request can't delay the connect-popup flow.
      trpc: { context: { skipBatching: true } },
    },
  );

  const hydratedApp = useMemo(() => {
    return {
      ...app,
      implementation: implementationData?.[0],
      implementationMeta: implementationMetaData?.[0],
    };
  }, [app, implementationData, implementationMetaData]);

  // Surface hydration loading so callers don't act on a partially-hydrated app
  // (e.g. clicking Connect before implementationMeta arrives would otherwise
  // see hasUpfrontFields=false momentarily).
  const isHydrating =
    queryEnabled && (isLoadingImplementation || isLoadingImplementationMeta);

  return {
    app: hydratedApp || undefined,
    isLoading: isLoading || isHydrating,
  };
}
